import { getLoginToken } from '@/components/shared/getToken';

import { baseForAskAi } from '../apiClients';
import { Endpoints } from '../../network';

export const askQuestion = async (data: {
  question: string;
  session_id?: string;
  book_id?: string;
  streaming?: boolean;
}) => {
  const urlWithQuery = `${Endpoints.askQuestion}`;

  const formData = new FormData();
  formData.append('question', data.question);
  if (data.book_id) formData.append('book_id', data.book_id);

  if (data.session_id) {
    formData.append('session_id', data.session_id);
  }
  if (data.streaming) {
    formData.append('streaming', String(data.streaming));
  }

  const response = await baseForAskAi.post(urlWithQuery, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response?.data?.data;
};

export async function askQuestionStream(
  payload: {
    question: string;
    session_id?: string;
    book_id?: string;
    streaming?: boolean;
  },
  onEvent: (event: { type: string; data: any }) => void,
) {
  const formData = new FormData();
  formData.append('question', payload.question);

  const apiUrl = process.env.NEXT_PUBLIC_AI_CHAT;

  if (payload.book_id) formData.append('book_id', payload.book_id);
  if (payload.session_id) formData.append('session_id', payload.session_id);
  if (payload.streaming) formData.append('streaming', 'true');

  const token = await getLoginToken();

  const response = await fetch(apiUrl + '/api/ask_question', {
    method: 'POST',
    body: formData,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.body) {
    throw new Error('Streaming not supported');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');

  let buffer = '';
  const condition = true;
  while (condition) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const events = buffer.split('\n');
    buffer = events.pop() || '';

    for (const evt of events) {
      if (!evt.trim()) continue;

      // Handle SSE "data:" prefix
      if (evt.startsWith('data:')) {
        const raw = evt.replace(/^data:\s*/, '');

        // DONE signal
        if (raw === '[DONE]') {
          onEvent({ type: 'DONE', data: null });
          return;
        }

        let parsed: any;
        try {
          parsed = JSON.parse(raw);
        } catch {
          parsed = raw; // plain text chunk
        }

        // 🔽 Classify event by payload shape
        if (typeof parsed === 'string') {
          onEvent({ type: 'message', data: parsed });
        } else if (Array.isArray(parsed)) {
          onEvent({ type: 'source_documents', data: parsed });
        } else if (parsed?.message) {
          onEvent({ type: 'status', data: parsed });
        } else if (parsed?.result && parsed?.session_id) {
          onEvent({ type: 'message_data', data: parsed });
        } else {
          onEvent({ type: 'unknown', data: parsed });
        }
      }
    }
  }
}

export const getPrompts = async () => {
  const urlWithQuery = `${Endpoints.getPrompts}`;

  const response = await baseForAskAi.get(urlWithQuery);

  return response?.data?.suggested_queries_list;
};

export const getSessionList = async (page: number, pageSize = 20) => {
  const response = await baseForAskAi.get(
    `${Endpoints.askQuestionHistory}?page=${page}&page_size=${pageSize}`,
  );

  return response?.data;
};

export const getSessionMessages = async (
  sessionId: string,
  page: number,
  pageSize = 20,
) => {
  const response = await baseForAskAi.get(
    `${Endpoints.getSessionMessages}?session_id=${sessionId}&page=${page}&page_size=${pageSize}`,
  );

  return response?.data;
};

export const sendResponseFeedback = async (
  queryId: string,
  feedback: -1 | 0 | 1,
) => {
  const formData = new FormData();
  formData.append('query_id', queryId);
  formData.append('feedback', String(feedback));

  const response = await baseForAskAi.post(
    Endpoints.responseFeedback,
    formData,
    {
      headers: {
        Accept: 'application/json',
      },
    },
  );

  return response?.data;
};

export const updateSessionTitle = async (sessionId: string, title: string) => {
  const formData = new FormData();
  formData.append('session_id', sessionId);
  formData.append('title', title);

  return baseForAskAi.post(Endpoints.updateSessionTitle, formData);
};

export const deleteSession = async (sessionId: string) => {
  const formData = new FormData();
  formData.append('session_id', sessionId);

  return baseForAskAi.post(Endpoints.deleteSession, formData);
};
